import React, { useEffect, useState } from 'react';
import { collection, deleteDoc, doc, getDocs, increment, updateDoc } from 'firebase/firestore';
import { db, storage } from '../firebase';
import { deleteObject, ref } from 'firebase/storage';
import { message } from 'antd';
import ErrorPage from './NotFound';

export default function Admin({ isAdmin }) {
  const [selectedTab, setSelectedTab] = useState('contributions');
  const [users, setUsers] = useState([]);
  const [contributors, setContributors] = useState([]);
  const [contributorIDs, setContributorIDs] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [contribLoading, setContribLoading] = useState(false);

  useEffect(() => {
    document.title = 'Admin — SitsCom';
  }, []);

  const getUsersData = async () => {
    setUsersLoading(true);
    try {
      const snapshot = await getDocs(collection(db, 'users'));
      const data = [];
      snapshot.forEach((doc) => data.push(doc.data()));
      setUsers(data);
    } catch {
      message.error('Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  };

  const getContributorsData = async () => {
    setContribLoading(true);
    try {
      const snapshot = await getDocs(collection(db, 'contributions'));
      const data = [];
      const ids = [];
      snapshot.forEach((doc) => {
        ids.push(doc.id);
        data.push(doc.data());
      });
      setContributorIDs(ids);
      setContributors(data);
    } catch {
      message.error('Failed to load contributions');
    } finally {
      setContribLoading(false);
    }
  };

  const incrementContributions = async (user) => {
    try {
      const docRef = doc(db, 'users', user.uid);
      await updateDoc(docRef, { contributions: increment(1) });
    } catch (error) {
      console.error('Error incrementing:', error);
    }
  };

  const deleteFirebaseFile = async (fileUrl) => {
    if (String(fileUrl).includes('https://firebasestorage.googleapis.com')) {
      try {
        const fileRef = ref(storage, fileUrl);
        await deleteObject(fileRef);
      } catch (error) {
        console.error('Error deleting file:', error);
      }
    }
  };

  const handleReject = async (inst) => {
    try {
      for (const link of inst.contribution) {
        await deleteFirebaseFile(link);
      }
      const index = contributors.indexOf(inst);
      if (index > -1 && contributorIDs[index]) {
        await deleteDoc(doc(db, 'contributions', contributorIDs[index]));
      }
      setContributors(prev => prev.filter(c => c !== inst));
      setContributorIDs(prev => prev.filter((_, i) => i !== index));
      message.success('Contribution rejected');
    } catch (error) {
      message.error('Failed to reject contribution');
      console.error(error);
    }
  };

  const handleAccept = async (inst) => {
    try {
      await incrementContributions(inst);
      await handleReject(inst);
      message.success('Contribution accepted');
    } catch (error) {
      message.error('Failed to accept contribution');
    }
  };

  if (!isAdmin) return <ErrorPage />;

  const tabs = [
    {
      id: 'contributions',
      label: 'Contributions',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
        </svg>
      ),
      badge: contributors.length || null,
    },
    {
      id: 'users',
      label: 'Users',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      ),
    },
  ];

  return (
    <div className="admin-page">
      <aside className="admin-sidebar">
        <div className="admin-sidebar-title">Admin Panel</div>
        <ul className="admin-nav">
          {tabs.map((tab) => (
            <li key={tab.id}>
              <button
                className={selectedTab === tab.id ? 'active' : ''}
                onClick={() => setSelectedTab(tab.id)}
              >
                <span className="nav-icon">{tab.icon}</span>
                {tab.label}
                {tab.badge ? <span className="nav-badge">{tab.badge}</span> : null}
              </button>
            </li>
          ))}
        </ul>
      </aside>

      <main className="admin-content">
        {selectedTab === 'contributions' && (
          <>
            <div className="admin-content-header">
              <h2>Contributions</h2>
              <div className="header-actions">
                <button
                  className="btn btn-secondary"
                  onClick={getContributorsData}
                  disabled={contribLoading}
                >
                  {contribLoading ? 'Loading...' : 'Refresh'}
                </button>
              </div>
            </div>

            <div className="admin-stats">
              <div className="stat-card">
                <div className="stat-label">Pending</div>
                <div className="stat-value">{contributors.length}</div>
              </div>
            </div>

            {contributors.length === 0 && !contribLoading && (
              <div className="empty-state">
                <h3>No pending contributions</h3>
                <p>Click "Refresh" to load contributions from the database.</p>
              </div>
            )}

            {contributors.map((item, index) => (
              <div className="approval-card fade-in" key={index}>
                <div className="approval-header">
                  <div className="approval-info">
                    <div className="approval-name">{item.name}</div>
                    <div className="approval-meta">
                      {item.email} &middot; {item.uid !== 'Anonymous' ? item.uid.substring(0, 8) + '...' : 'Anonymous'}
                    </div>
                  </div>
                  <span className={`badge ${item.email === 'Anonymous' ? 'badge-warning' : 'badge-success'}`}>
                    {item.email === 'Anonymous' ? 'Anonymous' : 'Registered'}
                  </span>
                </div>

                <div className="approval-links">
                  {item.contribution.map((link, i) => (
                    <div className="approval-link" key={i}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                      </svg>
                      <a href={link} target="_blank" rel="noreferrer">
                        {String(link).substring(0, 80)}{link.length > 80 ? '...' : ''}
                      </a>
                    </div>
                  ))}
                </div>

                <div className="approval-actions">
                  <button className="btn btn-secondary btn-sm" onClick={() => handleReject(item)}>
                    Reject
                  </button>
                  {item.email !== 'Anonymous' && (
                    <button className="btn btn-primary btn-sm" onClick={() => handleAccept(item)}>
                      Accept
                    </button>
                  )}
                  {item.email === 'Anonymous' && (
                    <button className="btn btn-danger btn-sm" onClick={() => handleReject(item)}>
                      Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </>
        )}

        {selectedTab === 'users' && (
          <>
            <div className="admin-content-header">
              <h2>Users</h2>
              <div className="header-actions">
                <button
                  className="btn btn-secondary"
                  onClick={getUsersData}
                  disabled={usersLoading}
                >
                  {usersLoading ? 'Loading...' : 'Load Users'}
                </button>
              </div>
            </div>

            <div className="admin-stats">
              <div className="stat-card">
                <div className="stat-label">Total Users</div>
                <div className="stat-value">{users.length}</div>
              </div>
            </div>

            {users.length > 0 && (
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>UID</th>
                      <th>Contributions</th>
                      <th>Saved</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user, i) => (
                      <tr key={i}>
                        <td style={{ fontWeight: 500 }}>{user.displayName || '—'}</td>
                        <td>{user.email || '—'}</td>
                        <td style={{ fontFamily: 'var(--font-mono)', fontSize: '12px', color: 'var(--text-tertiary)' }}>
                          {user.uid?.substring(0, 12)}...
                        </td>
                        <td>
                          <span className="badge">{user.contributions || 0}</span>
                        </td>
                        <td style={{ color: 'var(--text-tertiary)', fontSize: '13px' }}>
                          {user.saved?.length || 0} items
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {users.length === 0 && !usersLoading && (
              <div className="empty-state">
                <h3>No users loaded</h3>
                <p>Click "Load Users" to fetch user data from the database.</p>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
